import dayjs from 'dayjs';
import { useMemo } from 'react';
import { Avatar, Button, FlexRow, IconButton, Text } from '@epam/promo';
import { ReactComponent as DeleteIcon } from '@epam/assets/icons/common/action-delete-18.svg';
import { useUserStore } from 'store/user.store';
import { resetDraft, useTaskStore } from 'store/task.store';
import { useModal } from 'hooks/ui/useModal';
import {
  useTaskDeleteQuery,
  useTaskCreateQuery,
  useTaskReviewQuery,
  useTaskUpdateQuery,
  useTaskApproveQuery,
  useTaskRejectQuery,
  useTaskReopenQuery,
  useTaskConditionalReviewQuery,
} from 'hooks';
import css from './FormHeader.module.scss';

export const FormHeader = () => {
  const modal = useModal();
  const errors = useTaskStore((state) => state.errors);
  const currentUser = useUserStore((state) => state.user);
  const draftTask = useTaskStore((state) => state.draft);
  const fieldValidity = useTaskStore((state) => state.fieldValidity);
  const { mutate: sendTaskForReview } = useTaskReviewQuery();
  const { mutate: sendTaskWithCondition } = useTaskConditionalReviewQuery();
  const { mutate: createTask } = useTaskCreateQuery({ keepActive: true });
  const { mutate: updateTask } = useTaskUpdateQuery({ keepActive: true });
  const { mutate: deleteTask } = useTaskDeleteQuery();
  const { mutate: approveTask } = useTaskApproveQuery();
  const { mutate: rejectTask } = useTaskRejectQuery();
  const { mutate: reopenTask } = useTaskReopenQuery();

  const hasErrors = useMemo(
    () => Object.values(errors).some((field) => field.length > 0),
    [errors],
  );
  const isFormValid = useMemo(
    () => Object.values(fieldValidity).every((field) => field === true),
    [fieldValidity],
  );

  return (
    <FlexRow cx={css.form} padding="24" vPadding="12">
      <FlexRow>
        <FlexRow spacing="6">
          <Text color="gray60" font="sans" fontSize="12" lineHeight="18">
            Last edited by
          </Text>
          <Text
            color="gray60"
            font="sans-semibold"
            fontSize="12"
            lineHeight="18">
            {dayjs(draftTask.updatedAt || undefined).format('MMM DD, YYYY')}
          </Text>
          <Avatar
            alt={draftTask?.author?.id || currentUser.id}
            img={draftTask?.author?.avatar || currentUser.avatar}
            size="24"
          />
          <Text
            color="gray60"
            font="sans-semibold"
            fontSize="12"
            lineHeight="18">
            {`${draftTask?.author?.firstName || currentUser.firstName} ${
              draftTask?.author?.lastName || currentUser.lastName
            }`}
          </Text>
        </FlexRow>
      </FlexRow>
      <FlexRow spacing="6">
        {(draftTask?.status === 'DRAFT' ||
          draftTask?.status === 'NOT_SAVED') && (
          <>
            <Button
              caption="Save as draft"
              color="blue"
              fill="white"
              isDisabled={
                hasErrors || draftTask.validator === 'CONTAINS'
                  ? !draftTask.title.trim().length || !draftTask.answer.length
                  : !draftTask.title.trim().length
              }
              onClick={() =>
                modal.confirm({
                  text:
                    draftTask?.status === 'NOT_SAVED'
                      ? `Are you sure you want to save ${draftTask.title} as Draft?`
                      : 'Are you sure you want to update this task?',
                  highlight: 'Draft',
                  successAction: 'Yes',
                  onSuccess: () =>
                    !draftTask.id
                      ? createTask(draftTask)
                      : updateTask({ id: draftTask.id, task: draftTask }),
                })
              }
            />
            <Button
              caption="Save for approve"
              color="green"
              isDisabled={!isFormValid}
              onClick={() =>
                modal.confirm({
                  text: `Are you sure you want to save ${draftTask.title} for Approve?
									You will not be able to edit it anymore.`,
                  highlight: 'Approve',
                  successAction: 'Yes',
                  onSuccess: () =>
                    draftTask?.status === 'NOT_SAVED'
                      ? sendTaskWithCondition({
                          task: draftTask,
                          new: !draftTask.id,
                        })
                      : sendTaskForReview({ task: draftTask }),
                })
              }
            />
          </>
        )}
        {currentUser.role === 'SUPER_ADMIN' &&
          draftTask?.status === 'PENDING_APPROVAL' && (
            <>
              <Button
                caption="Approve"
                color="green"
                isDisabled={false}
                onClick={() =>
                  modal.confirm({
                    text: `Are you sure you want to Approve ${draftTask.title}?`,
                    highlight: 'Approve',
                    successAction: 'Yes',
                    onSuccess: () => approveTask({ id: draftTask.id }),
                  })
                }
              />
              <Button
                caption="Reject"
                color="red"
                fill="white"
                onClick={() =>
                  modal.confirm({
                    text: `Are you sure you want to Reject ${draftTask.title}? 
										This action will reopen task for editing.`,
                    highlight: 'Reject',
                    successAction: 'Yes',
                    onSuccess: () => rejectTask({ id: draftTask.id }),
                  })
                }
              />
            </>
          )}
        {currentUser.role === 'SUPER_ADMIN' &&
          draftTask?.status === 'APPROVED' && (
            <Button
              caption="Send to rework"
              color="red"
              fill="white"
              onClick={() =>
                modal.confirm({
                  text: 'Are you sure you want to reopen this task?',
                  highlight: 'this',
                  successAction: 'Yes',
                  onSuccess: () => reopenTask({ id: draftTask.id }),
                })
              }
            />
          )}
        {currentUser.role === 'SUPER_ADMIN' && (
          <IconButton
            color="red"
            cx={css['form__button-icon']}
            icon={DeleteIcon}
            onClick={() =>
              draftTask.id
                ? modal.delete({
                    text: `Are you sure you want to delete ${draftTask.title}?`,
                    highlight: draftTask.title,
                    successAction: 'Yes',
                    onSuccess: () => deleteTask({ id: draftTask.id }),
                  })
                : modal.delete({
                    text: 'Are you sure you want to discard this draft?',
                    highlight: 'discard',
                    successAction: 'Yes',
                    onSuccess: () => resetDraft(),
                  })
            }
          />
        )}
      </FlexRow>
    </FlexRow>
  );
};
